import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { deleteAccount } from '../../bridge';


const SettingsTab = ({ email }) => {
    const { isSubscribed, logout } = useAuth();
    const navigate = useNavigate();

    // Subscription management
    const [portalLoading, setPortalLoading] = useState(false);
    const [upgradeLoading, setUpgradeLoading] = useState(false);


    // Delete account
    const [deleteStep, setDeleteStep] = useState(0); // 0=idle, 1=confirm, 2=deleting
    const [deleteError, setDeleteError] = useState('');

    const handleManageSubscription = async () => {
        setPortalLoading(true);
        try {
            const { supabase } = await import('../../supabase');
            const { data, error } = await supabase.functions.invoke('create-portal-session', { body: { email } });
            if (error || !data?.url) throw new Error();
            window.location.href = data.url;
        } catch {
            alert('Could not open the billing portal. Please try again.');
        } finally {
            setPortalLoading(false);
        }
    };

    const handleUpgrade = async () => {
        setUpgradeLoading(true);
        try {
            const { supabase } = await import('../../supabase');
            const { data, error } = await supabase.functions.invoke('create-checkout-session', { body: { email } });
            if (error || !data?.url) throw new Error();
            window.location.href = data.url;
        } catch {
            alert('Could not open checkout. Please try again.');
        } finally {
            setUpgradeLoading(false);
        }
    };


    const handleDeleteAccount = async () => {
        setDeleteStep(2);
        setDeleteError('');
        const ok = await deleteAccount(email);
        if (!ok) {
            setDeleteError('Something went wrong. Please try again.');
            setDeleteStep(1);
            return;
        }
        logout();
        navigate('/', { replace: true });
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

            {/* ── Subscription ─────────────────────────────────────────── */}
            <div className="manage-card">
                <h2 className="manage-card-title">Subscription</h2>
                <div className="settings-plan-row">
                    <div>
                        <p className="settings-plan-label">Current plan</p>
                        <span className={`sub-badge ${isSubscribed ? 'pro' : 'free'}`}>
                            {isSubscribed ? '⭐ Pro' : 'Free'}
                        </span>
                    </div>
                    {isSubscribed ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
                            <button
                                className="manage-save-btn"
                                onClick={handleManageSubscription}
                                disabled={portalLoading}
                            >
                                {portalLoading ? 'Opening…' : 'Manage subscription'}
                            </button>
                            <p className="settings-hint">Cancel, update payment method, or view invoices.</p>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
                            <button
                                className="sub-upgrade-btn"
                                onClick={handleUpgrade}
                                disabled={upgradeLoading}
                            >
                                {upgradeLoading ? 'Opening…' : 'Upgrade to Pro ⭐'}
                            </button>
                            <p className="settings-hint">Up to 9 categories · 20 products each</p>
                        </div>
                    )}
                </div>
            </div>

            {/* ── Danger zone ──────────────────────────────────────────── */}
            <div className="manage-card settings-danger-card">
                <h2 className="manage-card-title" style={{ color: '#b02828' }}>Danger zone</h2>
                <p style={{ fontSize: 14, color: '#5C4A3B', lineHeight: 1.6, margin: '0 0 16px' }}>
                    Permanently delete your account and all associated data. This action cannot be undone.
                </p>

                {deleteStep === 0 && (
                    <button className="small-btn danger" onClick={() => setDeleteStep(1)}>
                        Delete my account
                    </button>
                )}

                {deleteStep === 1 && (
                    <div className="settings-confirm-box">
                        <p className="settings-confirm-text">
                            Are you sure? All your categories, products, and business info will be permanently deleted.
                        </p>
                        {deleteError && (
                            <p style={{ fontSize: 13, color: '#b02828', margin: '0 0 10px' }}>{deleteError}</p>
                        )}
                        <div style={{ display: 'flex', gap: 10 }}>
                            <button className="small-btn danger" onClick={handleDeleteAccount}>
                                Yes, delete everything
                            </button>
                            <button className="small-btn" onClick={() => { setDeleteStep(0); setDeleteError(''); }}>
                                Cancel
                            </button>
                        </div>
                    </div>
                )}

                {deleteStep === 2 && (
                    <p style={{ fontSize: 14, color: '#9C8677' }}>Deleting your account…</p>
                )}
            </div>

        </div>
    );
};

export default SettingsTab;
